import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ShoppingBasket } from "lucide-react";
import { NAV, waLink } from "@/lib/site";
import { useOrder } from "@/context/OrderContext";
import { useLang, LangToggle } from "@/context/LangContext";

const Logo = () => (
  <Link to="/" data-testid="navbar-logo-link" className="shrink-0">
    <img src="/assets/img/logo.webp" alt="Minaqu Indonesia" className="h-9 sm:h-10 w-auto" />
  </Link>
);

const BasketBtn = () => {
  const { count, setOpen } = useOrder();
  const { t } = useLang();
  return (
    <button
      data-testid="header-order-list-btn"
      onClick={() => setOpen(true)}
      className="relative inline-flex items-center gap-2 rounded-full border border-leaf/25 bg-white px-3.5 py-2 text-sm font-semibold text-[var(--leaf-deep)] transition-colors hover:bg-[var(--mint)]"
    >
      <ShoppingBasket className="h-4 w-4" />
      <span className="hidden sm:inline">{t("Pesanan", "Order")}</span>
      <span data-testid="header-order-count" className="grid h-5 min-w-5 place-items-center rounded-full bg-[var(--leaf)] px-1 text-[11px] font-bold text-white">
        {count}
      </span>
    </button>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { t } = useLang();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header data-testid="main-navbar" className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-500 ${scrolled || open ? "bg-white/85 shadow-[0_1px_0_rgba(15,122,53,0.12)] backdrop-blur-xl" : "bg-white/60 backdrop-blur-md"}`}>
      <div className="mx-auto flex h-16 sm:h-[72px] max-w-[1400px] items-center justify-between gap-4 px-5 sm:px-8">
        <Logo />
        <nav className="hidden xl:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end data-testid={`nav-link-${n.id}`}
              className={({ isActive }) => `relative rounded-full px-3.5 py-2 font-display text-[13px] font-bold tracking-wide transition-colors ${isActive ? "bg-[var(--leaf)] text-white" : "text-[var(--ink)] hover:text-[var(--leaf)]"}`}>
              {t(n.label, n.en)}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <LangToggle />
          <a href={waLink()} target="_blank" rel="noreferrer" data-testid="header-whatsapp-cta"
            className="hidden md:inline-flex items-center rounded-full bg-[var(--leaf)] px-4 py-2 font-display text-[13px] font-bold text-white transition-transform hover:-translate-y-0.5">
            WhatsApp
          </a>
          <BasketBtn />
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)} className="xl:hidden grid h-10 w-10 place-items-center rounded-full border border-leaf/25 bg-white" aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav data-testid="mobile-menu" initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="xl:hidden overflow-hidden border-t border-leaf/10 bg-white">
            <div className="px-5 py-4 flex flex-col">
              {NAV.map((n, i) => (
                <NavLink key={n.to} to={n.to} end data-testid={`mobile-nav-link-${n.id}`}
                  className={({ isActive }) => `flex items-baseline gap-4 border-b border-leaf/10 py-3 font-display text-2xl font-extrabold ${isActive ? "text-[var(--leaf)]" : "text-[var(--ink)]"}`}>
                  <span className="text-xs font-bold text-[var(--leaf)]">0{i + 1}</span>{t(n.label, n.en)}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
